/**
 * Where to send the user after a B2C redirect round trip.
 *
 * The value travels inside the MSAL `state` parameter, so it comes back from an
 * external system: only same-origin, absolute paths are accepted (no open redirect).
 */
export function sanitizeReturnTo(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > 2048) {
    return null;
  }
  // Must be a path on this origin: "/x", never "//host", "/\host" or "http:..."
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return null;
  }
  return value;
}

interface AuthState {
  v: 1;
  returnTo: string;
}

export function encodeAuthState(returnTo: string): string {
  const safe = sanitizeReturnTo(returnTo) ?? '/';
  const state: AuthState = { v: 1, returnTo: safe };
  return JSON.stringify(state);
}

export function decodeAuthState(state: string | null | undefined): string | null {
  if (!state) {
    return null;
  }
  try {
    const parsed = JSON.parse(state) as Partial<AuthState>;
    return parsed?.v === 1 ? sanitizeReturnTo(parsed.returnTo) : null;
  } catch {
    return null;
  }
}

export function currentPath(): string {
  return window.location.pathname + window.location.search;
}
